/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spin, Empty } from 'antd';
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  Tooltip,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FitParser from 'fit-file-parser';
import moment from 'moment';
import './FitTrackMap.less';

// 修复 Leaflet 默认 icon 路径
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// 高德中文瓦片
const AMAP_TILE_URL =
  'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=7&x={x}&y={y}&z={z}';
const AMAP_SUBDOMAINS = ['1', '2', '3', '4'];

const startIcon = L.divIcon({
  className: 'fit-start-icon',
  html: '<div style="background:#52c41a;width:16px;height:16px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const endIcon = L.divIcon({
  className: 'fit-end-icon',
  html: '<div style="background:#ff4d4f;width:16px;height:16px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// 速度颜色
const getSpeedColor = (speed) => {
  if (!speed) return '#3388ff';
  if (speed < 5) return '#52c41a';
  if (speed < 10) return '#faad14';
  return '#ff4d4f';
};

const FitTrackMap = ({ visible, onClose, fileUrl, pointCount }) => {
  const [loading, setLoading] = useState(false);
  const [fitData, setFitData] = useState(null);
  const [error, setError] = useState(null);

  // 拉取并解析 FIT 文件
  useEffect(() => {
    if (!visible || !fileUrl) return;

    const fetchAndParse = async () => {
      setLoading(true);
      setError(null);
      setFitData(null);

      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();

        const fitParser = new FitParser({
          force: true,
          speedUnit: 'km/h',
          lengthUnit: 'km',
          elapsedRecordField: true,
          mode: 'both',
        });

        try {
          fitParser.parse(arrayBuffer, (parseErr, data) => {
            if (parseErr) {
              console.error('FIT解析失败:', parseErr);
              setError(parseErr.message || 'FIT文件解析失败');
              setLoading(false);
              return;
            }

            const records = data?.records || [];

            const points = records
              .filter((r) => {
                const lat = r.position_lat;
                const lng = r.position_long;
                return lat != null && lng != null && lat !== 0 && lng !== 0;
              })
              .map((r) => ({
                lat: r.position_lat,
                lng: r.position_long,
                timestamp: r.timestamp,
                altitude: r.altitude,
                speed: r.speed,
                heartRate: r.heart_rate,
              }));

            console.log(
              '[FitTrackMap] GPS有效点数:',
              points.length,
              '首点:',
              points[0],
              '末点:',
              points[points.length - 1],
            );

            if (points.length === 0) {
              setError('FIT文件中无有效GPS坐标');
            } else {
              setFitData({
                points,
                startTime: points[0]?.timestamp,
                endTime: points[points.length - 1]?.timestamp,
                totalPoints: points.length,
              });
            }
            setLoading(false);
          });
        } catch (syncErr) {
          console.error('FIT同步解析异常:', syncErr);
          setError(
            syncErr.message || 'FIT文件格式不支持（Unimplemented type）',
          );
          setLoading(false);
        }
      } catch (err) {
        console.error('拉取FIT文件失败:', err);
        setError(err.message || '拉取FIT文件失败');
        setLoading(false);
      }
    };

    fetchAndParse();
  }, [visible, fileUrl]);

  // 地图中心与范围
  const mapBounds = useMemo(() => {
    if (!fitData?.points?.length) return null;
    const lats = fitData.points.map((p) => p.lat);
    const lngs = fitData.points.map((p) => p.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const center = [(minLat + maxLat) / 2, (minLng + maxLng) / 2];

    // 单点时扩展 bounds，防止零面积导致地图不渲染
    const padding = 0.002;
    const bounds =
      maxLat - minLat < padding && maxLng - minLng < padding
        ? [
            [minLat - padding, minLng - padding],
            [maxLat + padding, maxLng + padding],
          ]
        : [
            [minLat, minLng],
            [maxLat, maxLng],
          ];

    return { center, bounds };
  }, [fitData]);

  // 按速度分色轨迹段
  const coloredSegments = useMemo(() => {
    if (!fitData?.points?.length || fitData.points.length < 2) return [];
    const segments = [];
    for (let i = 0; i < fitData.points.length - 1; i++) {
      const from = fitData.points[i];
      const to = fitData.points[i + 1];
      const speed = from.speed || to.speed;
      segments.push({
        positions: [
          [from.lat, from.lng],
          [to.lat, to.lng],
        ],
        color: getSpeedColor(speed),
        speed,
      });
    }
    return segments;
  }, [fitData]);

  // 轨迹信息
  const fileInfo = useMemo(() => {
    if (!fitData) return null;
    const dist =
      fitData.points.length > 1
        ? (() => {
            let d = 0;
            for (let i = 1; i < fitData.points.length; i++) {
              const p1 = fitData.points[i - 1];
              const p2 = fitData.points[i];
              const R = 6371000;
              const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
              const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
              const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos((p1.lat * Math.PI) / 180) *
                  Math.cos((p2.lat * Math.PI) / 180) *
                  Math.sin(dLng / 2) *
                  Math.sin(dLng / 2);
              d += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            }
            return d;
          })()
        : 0;
    const duration =
      fitData.endTime && fitData.startTime
        ? fitData.endTime.getTime() - fitData.startTime.getTime()
        : null;
    return {
      totalPoints: fitData.totalPoints,
      distance:
        dist < 1000 ? `${Math.round(dist)}m` : `${(dist / 1000).toFixed(2)}km`,
      duration: duration ? moment.duration(duration).humanize() : '-',
      startTime: fitData.startTime
        ? moment(fitData.startTime).format('HH:mm:ss')
        : '-',
      endTime: fitData.endTime
        ? moment(fitData.endTime).format('HH:mm:ss')
        : '-',
    };
  }, [fitData]);

  const startPoint = fitData?.points?.[0];
  const endPoint = fitData?.points?.[fitData.points.length - 1];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title="GPS轨迹回放"
      width={800}
      footer={null}
      destroyOnClose
      centered
      className="fit-track-map-dialog"
    >
      <Spin spinning={loading}>
        <div className="fit-track-content">
          {error ? (
            <Empty description={error} />
          ) : fitData && fitData.points.length > 0 ? (
            <>
              {fileInfo && (
                <div className="fit-info-bar">
                  <span>轨迹点：{fileInfo.totalPoints}</span>
                  <span>总距离：{fileInfo.distance}</span>
                  <span>时长：{fileInfo.duration}</span>
                  <span>
                    时间：{fileInfo.startTime} ~ {fileInfo.endTime}
                  </span>
                  {pointCount !== undefined && (
                    <span>原始点数：{pointCount}</span>
                  )}
                </div>
              )}

              <div className="fit-legend">
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ background: '#52c41a' }}
                  />
                  慢速(&lt;5km/h)
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ background: '#faad14' }}
                  />
                  中速(5-10km/h)
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ background: '#ff4d4f' }}
                  />
                  快速(&gt;10km/h)
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{
                      background: '#52c41a',
                      borderRadius: '50%',
                      width: 10,
                      height: 10,
                    }}
                  />
                  起点
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{
                      background: '#ff4d4f',
                      borderRadius: '50%',
                      width: 10,
                      height: 10,
                    }}
                  />
                  终点
                </span>
              </div>

              {mapBounds && (
                <div className="fit-map-wrapper">
                  <MapContainer
                    center={mapBounds.center}
                    bounds={mapBounds.bounds}
                    zoom={15}
                    scrollWheelZoom={true}
                    style={{ height: 450, width: '100%' }}
                  >
                    <TileLayer
                      attribution="© 高德地图"
                      url={AMAP_TILE_URL}
                      subdomains={AMAP_SUBDOMAINS}
                      maxZoom={19}
                      maxNativeZoom={18}
                      minZoom={2}
                    />

                    {coloredSegments.map((seg, idx) => (
                      <Polyline
                        key={idx}
                        positions={seg.positions}
                        pathOptions={{
                          color: seg.color,
                          weight: 4,
                          opacity: 0.8,
                        }}
                      >
                        <Tooltip sticky>
                          {seg.speed ? `${seg.speed.toFixed(1)} km/h` : '-'}
                        </Tooltip>
                      </Polyline>
                    ))}

                    {startPoint && (
                      <Marker
                        position={[startPoint.lat, startPoint.lng]}
                        icon={startIcon}
                      >
                        <Popup>
                          <div>
                            <b>起点</b>
                            <br />
                            时间：
                            {moment(startPoint.timestamp).format('HH:mm:ss')}
                            <br />
                            速度：
                            {startPoint.speed
                              ? `${startPoint.speed.toFixed(1)} km/h`
                              : '-'}
                            {startPoint.heartRate && (
                              <>
                                <br />
                                心率：{startPoint.heartRate} bpm
                              </>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {endPoint && endPoint !== startPoint && (
                      <Marker
                        position={[endPoint.lat, endPoint.lng]}
                        icon={endIcon}
                      >
                        <Popup>
                          <div>
                            <b>终点</b>
                            <br />
                            时间：
                            {moment(endPoint.timestamp).format('HH:mm:ss')}
                            <br />
                            速度：
                            {endPoint.speed
                              ? `${endPoint.speed.toFixed(1)} km/h`
                              : '-'}
                            {endPoint.heartRate && (
                              <>
                                <br />
                                心率：{endPoint.heartRate} bpm
                              </>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </div>
              )}
            </>
          ) : !loading ? (
            <Empty description="暂无轨迹数据" />
          ) : null}
        </div>
      </Spin>
    </Modal>
  );
};

export default FitTrackMap;
